import { useRef } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/mocks/videos";
import { categoryMeta } from "@/mocks/videos";

interface HotSectionProps {
  title: string;
  subtitle: string;
  videos: Video[];
  morePath: string;
}

export default function HotSection({ title, subtitle, videos, morePath }: HotSectionProps) {
  const scroller = useRef<HTMLDivElement>(null);

  const scrollBy = (dir: number) => {
    if (!scroller.current) return;
    scroller.current.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  if (videos.length === 0) return null;

  return (
    <section className="w-full px-4 md:px-8 py-10 md:py-14">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 flex items-center gap-2">
            <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
            {title}
          </h2>
          <p className="text-sm text-foreground-600 mt-2">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to={morePath} className="hidden sm:flex text-sm text-primary-600 font-medium items-center gap-1 hover:text-primary-700 cursor-pointer whitespace-nowrap">
            查看全部 <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
            aria-label="向左滚动"
          >
            <i className="ri-arrow-left-s-line text-lg" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
            aria-label="向右滚动"
          >
            <i className="ri-arrow-right-s-line text-lg" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div ref={scroller} className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
        {videos.map((video, idx) => (
          <Link
            key={video.id}
            to={`/detail/${video.id}`}
            className="group shrink-0 w-[180px] sm:w-[200px] cursor-pointer"
          >
            <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 mb-3">
              <img
                src={video.cover}
                alt={`${video.title}封面`}
                title={`${video.title} - 69视频`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <span className="absolute top-0 left-0 w-8 h-8 rounded-br-lg bg-primary-950/85 text-background-50 flex items-center justify-center font-heading font-bold text-sm">
                {idx + 1}
              </span>
              <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-accent-500 text-primary-950 text-xs font-bold">
                {video.rating.toFixed(1)}
              </span>
            </div>
            <h3 className="font-semibold text-sm text-foreground-950 line-clamp-1 group-hover:text-primary-600 transition-colors">
              {video.title}
            </h3>
            <p className="text-xs text-foreground-500 mt-1">
              {categoryMeta[video.type].label} · {video.year} · {video.region}
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
}